/*
 * Copyright (c) 2022-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Injectable} from '@angular/core';
import {User} from '../shared/model/user';
import {NavItem} from './header/nav-item';
import {POWERTRAIN_IO_MATRIX_DISPLAY_NAME} from '../model/io-matrix.models';


@Injectable({
  providedIn: 'root'
})
export class MenuService {
  private theUser: User = null;
  private navDataNoRoles: NavItem[] = [
    {
      key: 'home',
      label: 'Home',
      routerLink: '/',
      requireAllPermissions: false,
      permission: ['*']
    }
    ];
  private navData: NavItem[] = [
      ...this.navDataNoRoles,
    {
      key: 'psds-tools',
      label: 'PSDS Tools',
      requireAllPermissions: false,
      permission: ['ELECTDB_USER'],
      items: [
        {
          key: 'psds-build-check-tool',
          label: 'Build Check Tool',
          routerLink: 'buildCheckTool',
          requireAllPermissions: false,
          permission: ['ELECTDB_USER']
        },
        {
          key: 'psds-crm2',
          label: 'Consolidated RPO Matrix',
          routerLink: 'crm2',
          requireAllPermissions: false,
          permission: ['ELECTDB_USER']
        },
        {
          key: 'psds-netlist-compare',
          label: 'Netlist Compare',
          routerLink: 'netlistCompare',
          requireAllPermissions: false,
          permission: ['ELECTDB_USER']
        },
        {
          key: 'psds-identical-connector',
          label: 'Identical Connector',
          routerLink: 'identicalConnector',
          requireAllPermissions: false,
          permission: ['ELECTDB_USER']
        },
        {
          key: 'psds-vehicle-build-compare',
          label: 'Vehicle Build Compare',
          routerLink: 'vehicleBuildCompare',
          requireAllPermissions: false,
          permission: ['ELECTDB_USER']
        },
        {
          key: 'psds-extraction-compare',
          label: 'Extraction Compare Tool',
          routerLink: 'extractionCompare',
          requireAllPermissions: false,
          permission: ['ELECTDB_USER']
        },
        {
          key: 'psds-capital-component-usage',
          label: 'Capital Component Usage',
          routerLink: 'capitalComponentUsage',
          requireAllPermissions: false,
          permission: ['ELECTDB_USER']
        }
      ]
    },
    {
      key: 'component-grids',
      label: 'Component Grids',
      requireAllPermissions: false,
      permission: ['*'],
      items: [
        {
          key: 'component-grids-tools',
          label: 'Tools',
          requireAllPermissions: false,
          permission: ['COMPONENT_ADMIN'],
          items: [
            {
              key: 'component-grids-list-management',
              label: 'List Management',
              routerLink: 'listManagement',
              requireAllPermissions: false,
              permission: ['COMPONENT_ADMIN']
            }
          ]
        }
      ]
    },
      {
          key: 'reference-data',
          label: 'Reference Data',
          requireAllPermissions: false,
          permission: ['*'],
          items: []
      },
    {
      key: 'program-timing-tool',
      label: 'Program Timing Tool',
      requireAllPermissions: false,
      permission: ['COMP_DRE', 'KTS_USER', 'KTS_CREATOR', 'KTS_ADMIN'],
      items: [
        {
          key: 'program-timing-view-saved',
          label: 'View Saved Timing',
          routerLink: 'programRuns',
          requireAllPermissions: false,
          permission: ['COMP_DRE', 'KTS_USER', 'KTS_CREATOR', 'KTS_ADMIN']
        },
        {
          key: 'program-timing-create',
          label: 'Create Program Timing',
          routerLink: 'kts',
          requireAllPermissions: false,
          permission: ['KTS_CREATOR', 'KTS_ADMIN']
        },
        {
          key: 'program-timing-template-editor',
          label: 'Template Editor',
          routerLink: 'ktsEditor',
          requireAllPermissions: false,
          permission: ['KTS_ADMIN']
        },
        {
          key: 'program-timing-restricted-program-access',
          label: 'Restricted Program Access',
          routerLink: 'restrictedPrograms',
          requireAllPermissions: false,
          permission: ['KTS_ADMIN']
        },
        {
          key: 'program-timing-kts-admin',
          label: 'KTS Admin Page',
          routerLink: 'ktsAdmin',
          requireAllPermissions: false,
          permission: ['KTS_ADMIN']
        }
      ]
    },
    {
      key: 'gps-program-timing',
      label: 'GPS Program Timing',
      requireAllPermissions: false,
      permission: ['GPS_TIMING_USER', 'GPS_TIMING_ADMIN'],
      items: [
        {
          key: 'gps-program-timing-rpo-management',
          label: 'GPS RPO Management',
          routerLink: 'gpsRpoManagement',
          requireAllPermissions: false,
          permission: ['GPS_TIMING_USER', 'GPS_TIMING_ADMIN'],
        },
        {
          key: 'gps-program-timing-gps-timing',
          label: 'GPS Timing',
          routerLink: 'gpsTiming',
          requireAllPermissions: false,
          permission: ['GPS_TIMING_USER', 'GPS_TIMING_ADMIN'],
        }
        ]
    },
    {
      key: 'icd-program-timing',
      label: 'ICD Program Timing',
      routerLink: 'icdProgramTiming',
      requireAllPermissions: false,
      permission: ['ESCI_ADMIN'],
    },
    {
      key: 'icd-workflow',
      label: 'ICD Workflow',
      requireAllPermissions: false,
      // ELECTDB_USER is here for the read-only Aggregator Signal Table only; every other child keeps
      // its own narrower role list, so a plain internal user sees just that one entry.
      permission: ['ICD_ADMIN', 'ICD_ENGR', 'POWERTRAIN_IO_MATRIX_ICD_USER', 'POWERTRAIN_IO_MATRIX_ADMIN', 'AGG_IO_MATRIX_ICD_USER', 'AGGREGATOR_ADMIN', 'ESCI_ADMIN', 'ELECTDB_USER'],
      items: [
        {
          key: 'icd-workflow-io-matrix',
          label: POWERTRAIN_IO_MATRIX_DISPLAY_NAME,
          routerLink: 'ioMatrix',
          requireAllPermissions: false,
          permission: ['ICD_ADMIN', 'ICD_ENGR', 'POWERTRAIN_IO_MATRIX_ADMIN', 'ESCI_ADMIN'],
        },
        {
          key: 'icd-workflow-io-matrix-icds',
          label: 'IO Matrix ICDs',
          routerLink: 'ioMatrixIcds',
          requireAllPermissions: false,
          permission: ['ICD_ADMIN', 'ICD_ENGR', 'POWERTRAIN_IO_MATRIX_ICD_USER', 'POWERTRAIN_IO_MATRIX_ADMIN', 'ESCI_ADMIN'],
        },
        {
          key: 'icd-workflow-create-icds',
          label: 'Create ICDs',
          routerLink: 'createIcds',
          requireAllPermissions: false,
          permission: ['ICD_ADMIN', 'ICD_ENGR', 'POWERTRAIN_IO_MATRIX_ICD_USER', 'POWERTRAIN_IO_MATRIX_ADMIN', 'ESCI_ADMIN'],
        },
        {
          key: 'icd-workflow-aggregator-signal',
          label: 'Aggregator Signal Table',
          routerLink: 'aggregatorSignal',
          requireAllPermissions: false,
          permission: ['ELECTDB_USER'],
        },
        {
          key: 'icd-workflow-aggregator-io-matrix',
          label: 'Aggregator IO Matrix',
          routerLink: 'aggregatorIoMatrix',
          requireAllPermissions: false,
          permission: ['ICD_ADMIN', 'ICD_ENGR', 'AGGREGATOR_ADMIN', 'ESCI_ADMIN'],
        },
        {
          key: 'icd-workflow-create-aggregator-icds',
          label: 'Create Aggregator ICDs',
          routerLink: 'createAggregatorIcds',
          requireAllPermissions: false,
          permission: ['ICD_ADMIN', 'ICD_ENGR', 'AGG_IO_MATRIX_ICD_USER', 'ESCI_ADMIN'],
        }
      ]
    },
    {
      key: 'icd-reporting',
      label: 'ICD Reporting',
      requireAllPermissions: false,
      permission: ['ELECTDB_USER'],
      items: [
        {
          key: 'icd-reporting-search',
          label: 'ICD Search',
          routerLink: 'icdReporting',
          requireAllPermissions: false,
          permission: ['ELECTDB_USER'],
        }
      ]
    },
    {
      key: 'workload-timing',
      label: 'Workload Timing',
      routerLink: 'workloadTiming',
      requireAllPermissions: false,
      permission: ['WORKLOAD_MODEL_ADMIN'],
    },
    {
      key: 'it-admin-tools',
      label: 'IT Admin Tools ',
      requireAllPermissions: false,
      permission: ['ESCI_IT_ADMIN'],
      items: [
          {
              key: 'esci-admin-url-request-activity-report',
              label: 'URL Request Activity Report',
              routerLink: 'urlRequestActivityReport',
              requireAllPermissions: false,
              permission: ['ESCI_IT_ADMIN'],
          },
          {
              key: 'esci-admin-clear-caches',
              label: 'Clear Caches',
              routerLink: 'clearCaches',
              requireAllPermissions: false,
              permission: ['ESCI_IT_ADMIN'],
          },
          {
              key: 'esci-admin-import-file-data-zip',
              label: 'Import Files (Zip)',
              routerLink: 'importFileDataZip',
              requireAllPermissions: false,
              permission: ['ESCI_IT_ADMIN'],
          },
          {
              key: 'esci-admin-edit-configurations',
              label: 'Edit Configurations',
              routerLink: 'esciConfiguration',
              requireAllPermissions: false,
              permission: ['ESCI_IT_ADMIN'],
          },
          {
              key: 'esci-admin-release-schedule',
              label: 'Release Scheduling',
              routerLink: 'releaseSchedule',
              requireAllPermissions: false,
              permission: ['ESCI_IT_ADMIN'],
          },
          {
              key: 'esci-admin-change-roles',
              label: 'Change Roles',
              routerLink: 'changeRoles',
              requireAllPermissions: false,
              permission: ['ESCI_IT_ADMIN'],
          }
      ]
    },
    {
      key: 'my-icd-subscriptions',
      label: 'My ICD Subscriptions',
      routerLink: 'icdSubscriptions',
      requireAllPermissions: false,
      permission: ['ELECTDB_USER']
    },
    {
      key: 'back-to-esci',
      label: 'Back to eSCI',
      routerLink: 'backToEsci',
      requireAllPermissions: false,
      permission: ['*']
    }
  ];

  constructor() {
  }

  public setUser(aUser: User) {
    this.theUser = aUser;
  }

  getMenuItems() {
    if (!this.theUser.hasRoles()) {
      return this.navDataNoRoles;
    }
    const availableItems = [];
    this.navData.forEach(item => {
      const menuItem = this.checkPermissions(item);
      if (menuItem != null) {
        availableItems.push(menuItem);
      }
    });
    return availableItems;
  }

  private checkPermissions(navItem: NavItem) {
    let navigation: NavItem;
    const hasPerm = this.theUser != null &&
        this.theUser.checkRoles(navItem.permission, navItem.requireAllPermissions);
    if (hasPerm) {
      navigation = Object.assign({}, navItem);
      navigation.items = [];
      this.copyChildren(navigation, navItem);
      return null == navItem.items ? navItem : navigation;
    }
    return null;
  }

  private copyChildren(target: NavItem, source: NavItem) {
    if (null != source.items) {
      source.items.forEach(child => {
        const newChild = this.checkPermissions(child);
        if (null != newChild) {
          target.items.push(newChild);
        }
      });
    }
  }

}
